import React from 'react';
import {observer} from 'mobx-react';
import {ProductCustomizationState} from './render.def';
import {EP_INHERITED_LINE_HEIGHT, sanitizeEPContent, transformPtToCqh} from '../components/engraved-plates/ep-content-utils';
// IMPORTANT: MUST NOT import from s.ts (product store) - this renderer code is used in multiple apps for image rendering

interface Props {
    pcs: ProductCustomizationState;
    // Which engraved plate this overlay paints: 1 (default, EPContent at ep_xpos/ep_ypos)
    // or 2 (EP2Content at ep2_xpos/ep2_ypos, present only for templates with a second
    // plate). Both share the plate background image and ink colour (css.ep_*).
    plate?: 1 | 2;
}

// One plate's overlay inputs, picked by index so the render below is index-agnostic.
const plateFields = (pcs: ProductCustomizationState, plate: 1 | 2) =>
    plate === 2
        ? {present: pcs.HasEngravedPlate2, content: pcs.EP2Content, width: pcs.EP2Width, height: pcs.EP2Height, xpos: pcs.ep2_xpos, ypos: pcs.ep2_ypos, zoom: pcs.ep2_zoom, id: 'ep-oldstyle-overlay-2', heightPt: 0}
        : {present: pcs.HasEngravedPlate, content: pcs.EPContent, width: pcs.EPWidth, height: pcs.EPHeight, xpos: pcs.ep_xpos, ypos: pcs.ep_ypos, zoom: pcs.ep_zoom, id: 'ep-oldstyle-overlay', heightPt: pcs.css.ep_plateHeightPt};

// Old-style (CustomizationMethodID < 2) engraved-plate overlay. New-style (== 2) frames
// render the plate inside the customization canvas (renderEngravedPlate) and never use
// this; old/none frames are static photos, so the plate is painted over them here at the
// template's ep_xpos/ep_ypos (inches from the frame center, +X right / +Y down — the
// same coordinate model as UV lines). Percentage positioning against the shrink-wrapped
// image container keeps the overlay correct at any displayed size (responsive, zoom,
// headless imagegen). Mirrors the ep-template-preview markup: the plate background comes
// from applyEPBackground (css.ep_*) and the content HTML scales via the pt -> cqh
// container-query transform. Mounted twice by frame-old/frame-none (plate 1 and plate 2);
// the second plate is always an overlay at its own ep2_* position and zoom.
export const EPOverlay = observer(({pcs, plate = 1}: Props) => {
    const f = plateFields(pcs, plate);

    if (!pcs.ep_show_oldstyle || !f.present) {
        return null;
    }

    // JF_* are the frame's physical inches; there is no valid fallback (FrameDisplay_*
    // is pixels), so without them the overlay cannot be placed. The photo spans the
    // JF size PLUS the moulding ext width (e.g. JF 19.0" + ext 1.5" = 20.5" of image
    // width), so the ext width joins the denominator — without it the plate renders
    // oversized relative to the document. Multi-opening composites whose PNG aspect
    // differs from that aspect (see render_old_style) still scale slightly off; the
    // admin calibrates position visually, and the same math runs in headless renders,
    // so saved values reproduce consistently everywhere.
    const extW = pcs.MouldingExtWidth > 0 ? pcs.MouldingExtWidth : 0;
    const frameW = pcs.JF_Width + extW;
    const frameH = pcs.JF_Height + extW;

    if (!(frameW > 0) || !(frameH > 0)) {
        return null;
    }

    // ep_zoom scales the whole plate (box + text, which sizes in cqh against the box)
    // on the old-style photo only — for quirky old-style images whose proportions make
    // the true plate size look wrong. 1 = actual size, 0.95 = 95%, 1.1 = 110%.
    const zoom = f.zoom > 0 ? f.zoom : 1;
    const epW = f.width > 0 ? f.width : 3;
    const epH = f.height > 0 ? f.height : 2;
    const html = transformPtToCqh(sanitizeEPContent(f.content || ''));

    return (
        <div
            id={f.id}
            style={
                {
                    position: 'absolute',
                    left: `${50 + (f.xpos / frameW) * 100}%`,
                    top: `${50 + (f.ypos / frameH) * 100}%`,
                    transform: 'translate(-50%, -50%)',
                    width: `${((epW * zoom) / frameW) * 100}%`,
                    // The aspect-ratio gives the size-contained box a definite block size so
                    // the content's cqh font units resolve (see ep-template.tsx preview).
                    aspectRatio: `${epW} / ${epH}`,
                    backgroundImage: pcs.css.ep_backgroundImage,
                    backgroundSize: pcs.css.ep_backgroundSize || 'cover',
                    color: pcs.css.ep_color,
                    // the frame canvas's inherited line-height; the image generator's page has no Bootstrap 1.5
                    lineHeight: EP_INHERITED_LINE_HEIGHT,
                    containerType: 'size',
                    overflow: 'hidden',
                    zIndex: 5,
                    pointerEvents: 'none',
                    '--ep-h-pt': f.heightPt || Math.round(epH * 72),
                } as React.CSSProperties
            }
        >
            <div
                style={{
                    width: '100%',
                    height: '100%',
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                }}
                dangerouslySetInnerHTML={{__html: html}}
            />
        </div>
    );
});
